/**
 * Auth Store
 * Zustand store for authentication state management
 */

import { create } from 'zustand';
import type { User } from '@/types/api';

/**
 * Auth store state interface
 */
interface AuthState {
  /** Current authenticated user */
  user: User | null;
  /** JWT access token */
  accessToken: string | null;
  /** Token expiration timestamp (ISO 8601) */
  expiresAt: string | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth operation is in progress */
  isLoading: boolean;
  /** Current error message */
  error: string | null;
}

/**
 * Auth store actions interface
 */
interface AuthActions {
  /** Set the current user */
  setUser: (user: User | null) => void;
  /** Set the access token (also persists to localStorage) */
  setAccessToken: (token: string | null) => void;
  /** Login with user, token, and expiration */
  login: (user: User, token: string, expiresAt: string) => void;
  /** Logout and clear all auth state */
  logout: () => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Set error message */
  setError: (error: string | null) => void;
  /** Initialize auth state from localStorage */
  initializeFromStorage: () => void;
  /** Reset the store to initial state */
  reset: () => void;
}

/**
 * Initial state for auth store
 */
const initialState: AuthState = {
  user: null,
  accessToken: null,
  expiresAt: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

/**
 * Auth store combining state and actions
 */
export const useAuthStore = create<AuthState & AuthActions>((set, get) => ({
  ...initialState,

  setUser: (user) => {
    try {
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        localStorage.removeItem('user');
      }
    } catch {
      // localStorage might be unavailable
    }

    set({
      user,
      isAuthenticated: user !== null && get().accessToken !== null,
    });
  },

  setAccessToken: (token) => {
    if (token) {
      try {
        localStorage.setItem('accessToken', token);
      } catch {
        // localStorage might be unavailable
      }
    } else {
      try {
        localStorage.removeItem('accessToken');
      } catch {
        // localStorage might be unavailable
      }
    }

    set({
      accessToken: token,
      isAuthenticated: token !== null && get().user !== null,
    });
  },

  login: (user, token, expiresAt) => {
    try {
      localStorage.setItem('accessToken', token);
      localStorage.setItem('user', JSON.stringify(user));
    } catch {
      // localStorage might be unavailable
    }

    set({
      user,
      accessToken: token,
      expiresAt,
      isAuthenticated: true,
      error: null,
    });
  },

  logout: () => {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    } catch {
      // localStorage might be unavailable
    }

    set({
      user: null,
      accessToken: null,
      expiresAt: null,
      isAuthenticated: false,
      error: null,
    });
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setError: (error) => {
    set({ error });
  },

  initializeFromStorage: () => {
    try {
      const token = localStorage.getItem('accessToken');
      const userJson = localStorage.getItem('user');
      if (token) {
        const user = userJson ? (JSON.parse(userJson) as User) : null;
        set({
          accessToken: token,
          user,
          isAuthenticated: user !== null,
        });
      }
    } catch {
      // localStorage might be unavailable or user JSON is corrupt
    }
  },

  reset: () => {
    set(initialState);
  },
}));
